/** Tema (Luminoasă / Întunecată / Ca sistemul), persistată în localStorage. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
type Resolved = 'light' | 'dark';

interface ThemeCtx {
  pref: ThemePref;
  resolved: Resolved;
  setPref: (p: ThemePref) => void;
  toggle: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);
const KEY = 'rv-theme';

function systemResolved(): Resolved {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function apply(resolved: Resolved) {
  document.documentElement.dataset.theme = resolved;
  if (document.body) document.body.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#181411' : '#FBF8F3');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => {
    try {
      const stored = localStorage.getItem(KEY) as ThemePref | null;
      return stored ?? 'light';
    } catch {
      return 'light';
    }
  });
  const [resolved, setResolved] = useState<Resolved>(() =>
    pref === 'system' ? systemResolved() : (pref as Resolved),
  );

  useEffect(() => {
    const r = pref === 'system' ? systemResolved() : (pref as Resolved);
    setResolved(r);
    apply(r);
    if (pref !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const nr = systemResolved();
      setResolved(nr);
      apply(nr);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  const setPref = useCallback((p: ThemePref) => {
    try {
      localStorage.setItem(KEY, p);
    } catch {
      /* ignore */
    }
    setPrefState(p);
  }, []);

  const toggle = useCallback(() => {
    setPref(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setPref]);

  const value = useMemo(() => ({ pref, resolved, setPref, toggle }), [pref, resolved, setPref, toggle]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTheme trebuie folosit în ThemeProvider');
  return ctx;
}
